import useCreateTag from '@/api/mutations/tag/useCreateTag';
import useGetMyAvailableTags from '@/api/queries/me/useGetMyAvailableTags';
import { CreateTagInput } from '@/gql/graphql';
import { Button, ColourInput, Field, Input, Modal } from '@payaca/ambrosia';

import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import {
  VALID_HEX_COLOUR,
  VALID_TAG_CHARACTERS_REGEX,
} from '@payaca/constants/regexConstants';
import {
  getIsRequiredFieldValidator,
  getLengthFieldValidator,
  getRegexMatchFieldValidator,
  getValueMustNotExistInArrayFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';
import { getRandomTagColour } from '@payaca/helpers/tagHelper';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

export const CreateTagModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}> = ({ isOpen, onClose, onSuccess }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const initialFormState = useMemo(() => {
    const col = getRandomTagColour();

    return {
      name: '',
      colour: col,
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: isOpen
  }, [isOpen]);

  const { data: tags } = useGetMyAvailableTags({
    status: ['active', 'archived'],
  });

  const { existingActiveTagNames, existingArchivedTagNames } = useMemo(() => {
    if (!tags)
      return { existingActiveTagNames: [], existingArchivedTagNames: [] };

    return tags.reduce(
      (
        acc: {
          existingActiveTagNames: string[];
          existingArchivedTagNames: string[];
        },
        t
      ) => {
        if (t.archivedAt) {
          acc.existingArchivedTagNames.push(t.name);
        } else {
          acc.existingActiveTagNames.push(t.name);
        }

        return acc;
      },
      {
        existingActiveTagNames: [],
        existingArchivedTagNames: [],
      }
    );
  }, [tags]);

  useEffect(() => {
    if (isOpen) {
      setIsSubmitted(false);
    }
  }, [isOpen]);

  const fieldValidators = useMemo(() => {
    return {
      name: [
        getIsRequiredFieldValidator(),
        getRegexMatchFieldValidator(VALID_TAG_CHARACTERS_REGEX, {
          customErrorMessage: 'Invalid Tag name',
        }),
        getLengthFieldValidator({ min: 1, max: 30 }),
        getValueMustNotExistInArrayFieldValidator(existingActiveTagNames, {
          customErrorMessage: 'Tag name already exists',
        }),
        getValueMustNotExistInArrayFieldValidator(existingArchivedTagNames, {
          customErrorMessage: 'An archived Tag with this name already exists',
        }),
      ],
      colour: [getRegexMatchFieldValidator(VALID_HEX_COLOUR)],
    };
  }, [existingActiveTagNames, existingArchivedTagNames]);

  const { mutateAsync: createTagMutation } = useCreateTag();

  const onSubmit = useCallback(
    (input: CreateTagInput) => {
      setIsProcessing(true);
      setIsSubmitted(true);
      // oxlint-disable-next-line typescript-eslint(no-floating-promises) -- FIXME: pre-existing error: Promises must be awaited.
      createTagMutation(input)
        .then(() => {
          onClose();
          onSuccess?.();
        })
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [onSuccess, createTagMutation, onClose]
  );

  return (
    <Modal title="Create Tag" isOpen={isOpen} onClose={onClose}>
      <ValidatedForm
        fieldValidators={fieldValidators}
        initialFormState={initialFormState}
        renderFormContents={(
          isValid,
          formState,
          validationState,
          touchedState,
          onFieldChange,
          onFieldTouch
        ) => {
          return (
            <>
              <Modal.Body>
                <div className="flex flex-col gap-4">
                  <Field.Legacy
                    name="name"
                    validationState={
                      !isSubmitted &&
                      !validationState?.['name']?.isValid &&
                      touchedState?.['name']
                        ? {
                            isValid: false,
                            validationMessages:
                              validationState?.['name']?.errors,
                          }
                        : undefined
                    }
                  >
                    <Field.Label>Tag Name</Field.Label>
                    <Input
                      value={formState.name}
                      onChange={(value) => onFieldChange({ name: value })}
                      onBlur={() => onFieldTouch('name')}
                      onChangeTimeout={() => onFieldTouch('name')}
                    />
                  </Field.Legacy>
                  <Field.Legacy
                    name="colour"
                    validationState={
                      !validationState?.['colour']?.isValid &&
                      touchedState?.['colour']
                        ? {
                            isValid: false,
                            validationMessages:
                              validationState?.['colour']?.errors,
                          }
                        : undefined
                    }
                  >
                    <Field.Label>Colour</Field.Label>
                    <ColourInput
                      value={formState.colour || ''}
                      onChange={(value) => onFieldChange({ colour: value })}
                      onTouch={() => onFieldTouch('colour')}
                    ></ColourInput>
                  </Field.Legacy>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Modal.Footer.Actions>
                  <Button
                    isProcessing={isProcessing}
                    disabled={!isValid}
                    onClick={() => {
                      if (!isProcessing) {
                        onSubmit(formState);
                      }
                    }}
                  >
                    Save
                  </Button>
                </Modal.Footer.Actions>
              </Modal.Footer>
            </>
          );
        }}
      />
    </Modal>
  );
};
